import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import {
  Card,
  Row,
  CardBody,
  Col,
  Button,
  Alert,
  Form,
} from "reactstrap";
import { StateContext } from "../../utils/context";
import TenantContext from "../../utils/tenantContext";
import SubscriptionsFeatureLockAlerts from "../../components/subscriptionsFeatureLockAlerts";
import { stripeSubscriptionService } from "../../jwt/_services/stripeSubscription.service";
import { isSubscriptionFeatureLocked } from "../../utils/subscriptionUtils";
import ProductFields, {
  EMPTY_PRODUCT,
  productToDraft,
  draftToPayload,
} from "./productFields";
import "./stripeSubscriptionConfig.css";

const ProductUpdate = ({ match }) => {
  const { currentUser } = useContext(StateContext);
  const { tenant } = useContext(TenantContext);
  const featureLocked = isSubscriptionFeatureLocked(tenant, currentUser?.sla_level);
  const currentID = match.url.split("/")[2];
  const [draftProduct, setDraftProduct] = useState({ ...EMPTY_PRODUCT });
  const [loadedProduct, setLoadedProduct] = useState(null);
  const [tenantCurrency, setTenantCurrency] = useState("eur");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      stripeSubscriptionService.getProduct(currentID),
      stripeSubscriptionService.getConfig(),
    ])
      .then(([product, configData]) => {
        setLoadedProduct(product);
        setTenantCurrency(configData.currency || "eur");
      })
      .catch(() => setError("Failed to load product."))
      .finally(() => setLoading(false));
  }, [currentID]);

  useEffect(() => {
    if (loadedProduct && currentUser) {
      setDraftProduct(productToDraft(loadedProduct, currentUser));
    }
  }, [loadedProduct, currentUser]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!draftProduct.name || !draftProduct.amount || !draftProduct.description?.trim()) {
      setError("Product name, price, and description are required.");
      return;
    }

    setSaving(true);
    setError("");
    stripeSubscriptionService
      .updateProduct(currentID, draftToPayload(draftProduct, currentUser))
      .then(() => {
        setSuccess(true);
        setTimeout(() => setRedirect(true), 1000);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Failed to update product.");
      })
      .finally(() => setSaving(false));
  };

  if (redirect) {
    return <Redirect to="/products" />;
  }

  return (
    <div className="stripe-subscription-page">
      {featureLocked && (
        <SubscriptionsFeatureLockAlerts tenant={tenant} slaLevel={currentUser?.sla_level} />
      )}

      <Row>
        <Col sm={12}>
          <Card className="stripe-section-card">
            <CardBody>
              <h3 className="config-page-title mb-4">Edit Product</h3>
              {loading ? (
                <p className="text-muted">Loading...</p>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <ProductFields
                    draftProduct={draftProduct}
                    setDraftProduct={setDraftProduct}
                    tenantCurrency={tenantCurrency}
                    disabled={featureLocked}
                  />
                  <Row className="stripe-product-form-actions-row">
                    <Col lg={8}>
                      <div className="stripe-product-form-actions">
                        <Button color="success" type="submit" disabled={featureLocked || saving || loading}>
                          Save
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              )}
              <div style={{ paddingTop: "24px" }}>
                {success && (
                  <Alert color="success">Product updated successfully.</Alert>
                )}
                {error && <Alert color="danger">{error}</Alert>}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProductUpdate;
